import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Edit, Trash2, ArrowLeft, Download, MessageCircle } from 'lucide-react'
import { notesAPI, sensitiveAPI } from '../../services/api'
import localStorageService from '../../services/localStorageService'
import { useTabs } from '../../contexts/TabsContext'
import { useNotes } from '../../contexts/NotesContext'
import { useFolders } from '../../contexts/FoldersContext'
import { useSensitiveSession } from '../../contexts/SensitiveSessionContext'
import { useLocalMode } from '../../contexts/LocalModeContext'
import { useAuth } from '../../contexts/AuthContext'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import ChatWithNoteModal from '../../components/common/ChatWithNoteModal'
// Removed EditNotePasswordModal import - using browser popup instead

import toast from 'react-hot-toast'
import { SYNC_CONFIG } from '../../services/syncConfig'
import frontendSync from '../../services/frontendSync'
import imageUploadService, { preCacheImagesFromContent } from '../../services/imageUploadService'
import FlowchartNoteViewer from '../../components/viewers/FlowchartNoteViewer'
import { detectNoteType } from '../../utils/noteTypeDetection'

// Global password prompt tracking (shared across all component instances)
const globalPasswordPromptCache = new Map()

// Global cache for decrypted content to prevent re-render loops
// NOTE: This cache should NEVER be used for sensitive notes - they must always require a password
const globalDecryptedContentCache = new Map()

// Clear sensitive notes from global cache for security
const clearSensitiveNotesFromGlobalCache = () => {
  console.log('🔐 ViewNotePage: Clearing sensitive notes from global cache for security')
  // We can't easily identify which cached entries are sensitive without the note object,
  // so we'll clear the entire cache when dealing with sensitive notes
  globalDecryptedContentCache.clear()
}

// Cleanup expired password prompt cache entries
const cleanupPasswordPromptCache = () => {
  const now = Date.now()
  const fiveMinutes = 300000 // 5 minutes
  
  for (const [key, value] of globalPasswordPromptCache.entries()) {
    if (now - value.timestamp > fiveMinutes) {
      globalPasswordPromptCache.delete(key)
    }
  }
}

// Clean up cache every 5 minutes
setInterval(cleanupPasswordPromptCache, 300000)

/**
 * View Note Page Component
 * 
 * Read-only view of a specific note with edit and delete options.
 * Fetches note data by ID and displays the full content with rich text formatting.
 * Can work in both standalone mode (with URL params) and tab mode (with props).
 */
const ViewNotePage = ({ tabId = null, noteId: propNoteId = null }) => {
  const { id: urlId } = useParams()
  const navigate = useNavigate()
  const { openSettings } = useAuth()
  const { tabs, openTab, closeTab, updateTab } = useTabs()
  const { notes, deleteNote, getCachedNoteById, cacheSingleNote, fetchNotes, refreshSensitiveNotes } = useNotes()
  const { folders } = useFolders()
  const sensitiveSession = useSensitiveSession()
  const { isLocalMode, getLocalNote, getSensitivePassword, getLocalFile, updateLocalNote } = useLocalMode()
  
  // Use prop noteId if in tab mode, otherwise use URL param
  const noteId = propNoteId || urlId
  const isInTab = tabId !== null
  
  const [note, setNote] = useState(null)
  const [isDecrypted, setIsDecrypted] = useState(false)
  const hasLoadedNote = useRef(false)
  
  // Reset loaded flag when noteId changes
  useEffect(() => {
    hasLoadedNote.current = false
  }, [noteId])
  
  // Function to make content read-only by removing contenteditable attributes
  const makeContentReadOnly = useCallback((htmlContent) => {
    if (!htmlContent || typeof htmlContent !== 'string') {
      return htmlContent
    }
    
    // Remove contenteditable attributes from all elements
    let readOnlyContent = htmlContent.replace(/\s*contenteditable\s*=\s*["']?[^"'\s>]*["']?/gi, '')
    
    // Also remove any onclick handlers from table menu buttons to prevent interaction
    readOnlyContent = readOnlyContent.replace(/\s*onclick\s*=\s*["'][^"']*["']/gi, '')
    
    // Hide table menu buttons in view mode by adding a style
    readOnlyContent = readOnlyContent.replace(
      /(<button[^>]*class\s*=\s*["'][^"']*table-menu-button[^"']*["'][^>]*)/gi,
      '$1 style="display: none !important;"'
    )
    
    // Hide table floating menus in view mode
    readOnlyContent = readOnlyContent.replace(
      /(<div[^>]*class\s*=\s*["'][^"']*table-floating-menu[^"']*["'][^>]*)/gi,
      '$1 style="display: none !important;"'
    )
    
    return readOnlyContent
  }, [])

  // Custom setNote function that prevents overriding decrypted content with encrypted content
  const setNoteSafely = useCallback((newNote) => {
    if (!newNote) {
      setNote(null)
      setIsDecrypted(false)
      return
    }
    
    // Check if the new note is decrypted
    const newNoteIsDecrypted = !newNote.isEncrypted && !isContentEncrypted(newNote.content)
    
    // If we already have decrypted content and the new note is encrypted, don't override
    if (isDecrypted && newNoteIsDecrypted === false && isContentEncrypted(newNote.content)) {
      console.log('🔍 ViewNotePage: Preventing override of decrypted content with encrypted content')
      return
    }
    
    setNote(newNote)
    setIsDecrypted(newNoteIsDecrypted)
    
    console.log('🔍 ViewNotePage: Note updated:', {
      noteId: newNote.noteId,
      isEncrypted: newNote.isEncrypted,
      contentLength: newNote.content?.length,
      contentPreview: newNote.content?.substring(0, 50),
      isDecrypted: newNoteIsDecrypted
    })
  }, [isDecrypted])
  const [loading, setLoading] = useState(true)
  const [isDeleting, setIsDeleting] = useState(false)
  // Removed modal state - using browser popup instead
  const [pendingNote, setPendingNote] = useState(null)
  const [renderKey, setRenderKey] = useState(0)
  const lastRenderedContent = useRef(null)
  const isDecrypting = useRef(false)
  const [renderedContent, setRenderedContent] = useState(null)
  const [isChatModalOpen, setChatModalOpen] = useState(false)

  // Request deduplication and hot cache
  const pendingRequests = useRef(new Map())
  const hotCache = useRef(new Map())
  const maxHotCacheItems = 15
  const hotCacheTTL = 15 * 60 * 1000 // 15 minutes

  /**
   * Get note from hot memory cache (instant access)
   */
  const getHotCachedNote = (noteId) => {
    const cached = hotCache.current.get(noteId)
    if (cached && Date.now() - cached.timestamp < hotCacheTTL) {
      console.log('⚡ ViewNotePage: Note loaded from hot cache (instant)')
      return cached.note
    }
    return null
  }

  /**
   * Add note to hot memory cache
   */
  const addToHotCache = (note) => {
    // Simple LRU eviction
    if (hotCache.current.size >= maxHotCacheItems) {
      const firstKey = hotCache.current.keys().next().value
      hotCache.current.delete(firstKey)
    }
    
    hotCache.current.set(note.noteId, {
      note,
      timestamp: Date.now()
    })
    console.log('💾 ViewNotePage: Note added to hot cache')
  }

  /**
   * Background refresh from Drive if cached note is stale
   */
  const backgroundRefreshNote = async (id, baselineLastModified) => {
    if (!SYNC_CONFIG.USE_FRONTEND_SYNC || !baselineLastModified) return
    try {
      const fresh = await frontendSync.loadNoteById(id, { minLastModified: baselineLastModified })
      if (fresh) {
        // Update state/cache if fresher or different
        const current = getHotCachedNote(id) || note
        const currentTs = current?.lastModified ? new Date(current.lastModified).getTime() : 0
        const freshTs = fresh?.lastModified ? new Date(fresh.lastModified).getTime() : 0
        if (!current || freshTs > currentTs || fresh.content !== current.content) {
          // Decrypt the fresh note before setting state
          const decryptedFresh = await decryptNoteIfNeeded(fresh)
          
          setNoteSafely(decryptedFresh)
          addToHotCache(decryptedFresh)
          
          // Cache the encrypted version (not decrypted) for consistency
          try { await cacheSingleNote(fresh) } catch (_) {}
        }
      }
    } catch (_) {}
  }

  /**
   * Check if content appears to be encrypted
   */
  const isContentEncrypted = (content) => {
    if (!content || typeof content !== 'string') return false
    
    // Check if content looks like encrypted data (AES-GCM produces base64-like strings)
    const looksLikeHTML = content.includes('<') && content.includes('>')
    const looksLikePlainText = content.length < 100 && /^[a-zA-Z0-9\s.,!?-]+$/.test(content)
    const looksLikeEncrypted = content.length > 50 && /^[A-Za-z0-9+/]*={0,2}$/.test(content)
    
    // If it looks like encrypted data and doesn't look like HTML or plain text, it's likely encrypted
    return looksLikeEncrypted && !looksLikeHTML && !looksLikePlainText
  }

  /**
   * Check if a note is sensitive (in sensitive folder)
   */
  const isSensitiveNote = (note) => {
    if (!note?.folderId) return false
    
    const noteFolder = folders.find(f => f.folderId === note.folderId)
    return noteFolder?.folderType === 'SENSITIVE'
  }

  /**
   * Load note data when component mounts or sensitive session changes
   */
  useEffect(() => {
    console.log('🔍 ViewNotePage: Component mounted/updated with ID:', noteId)
    console.log('🔍 ViewNotePage: hasLoadedNote.current:', hasLoadedNote.current)
    console.log('🔍 ViewNotePage: sensitiveSession.isAuthenticated:', sensitiveSession.isAuthenticated)
    
    if (noteId && noteId !== 'undefined') {
      // Don't reload if we've already loaded this note
      if (hasLoadedNote.current) {
        console.log('🔍 ViewNotePage: Note already loaded, skipping loadNote in useEffect')
        return
      }
      
      // Don't reload if we're currently decrypting
      if (isDecrypting.current) {
        console.log('🔍 ViewNotePage: Currently decrypting, skipping loadNote in useEffect')
        return
      }
      
      console.log('🔍 ViewNotePage: useEffect triggered - isDecrypting:', isDecrypting.current)
      
      // Don't reload if we already have a decrypted note
      if (note && !note.isEncrypted && !isContentEncrypted(note.content)) {
        console.log('🔍 ViewNotePage: Note already decrypted, skipping loadNote in useEffect')
        hasLoadedNote.current = true
        return
      }
      
      // Don't reload if we have a decrypted note in hot cache
      const hotCachedDecryptedNote = getHotCachedNote(noteId)
      if (hotCachedDecryptedNote && !hotCachedDecryptedNote.isEncrypted && !isContentEncrypted(hotCachedDecryptedNote.content)) {
        console.log('🔍 ViewNotePage: Note already decrypted in hot cache, using it and skipping loadNote in useEffect')
        setNoteSafely(hotCachedDecryptedNote)
        hasLoadedNote.current = true
        
        // Update tab title if in tab mode
        if (isInTab && tabId && hotCachedDecryptedNote.title) {
          updateTab(tabId, { title: hotCachedDecryptedNote.title })
        }
        return
      }
      
      hasLoadedNote.current = true
      loadNote()
    } else {
      console.error('❌ ViewNotePage: Invalid note ID:', noteId)
      toast.error('Invalid note ID')
      if (isInTab && tabId) {
        closeTab(tabId)
      } else {
        navigate('/notes')
      }
    }
  }, [noteId, sensitiveSession.isAuthenticated]) // Removed folders dependency to prevent unnecessary reloads

  // Prevent hash navigation for attachment links
  useEffect(() => {
    const handleHashClick = (e) => {
      if (e.target.tagName === 'A' && e.target.getAttribute('href')?.startsWith('#attachment-')) {
        e.preventDefault()
        return false
      }
    }

    document.addEventListener('click', handleHashClick)
    return () => document.removeEventListener('click', handleHashClick)
  }, [])

  // Handle note link clicks to open linked notes in new tabs
  useEffect(() => {
    const handleNoteLinkClick = (e) => {
      const noteLink = e.target.closest('.note-link')
      if (noteLink) {
        e.preventDefault()
        e.stopPropagation()
        
        const linkedNoteId = noteLink.getAttribute('data-note-id')
        const linkedNoteTitle = noteLink.getAttribute('data-note-title')
        
        if (linkedNoteId && linkedNoteTitle) {
          console.log(`🔗 ViewNotePage: Opening linked note: ${linkedNoteTitle} (${linkedNoteId})`)
          
          // Open note in new tab
          openTab({
            type: 'view',
            noteId: linkedNoteId,
            title: linkedNoteTitle
          })
          
          toast.success(`Opening "${linkedNoteTitle}"`, {
            duration: 1500,
            style: {
              background: 'rgba(59, 130, 246, 0.5)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              color: 'white',
              borderRadius: '12px',
              padding: '12px 16px',
              fontSize: '14px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              border: '1px solid rgba(59, 130, 246, 0.3)'
            }
          })
        } else {
          console.warn('⚠️ ViewNotePage: Note link missing data attributes')
          toast.error('Invalid note link')
        }
      }
    }
    
    // Add click listener to document to catch clicks in the rich-text-display div
    document.addEventListener('click', handleNoteLinkClick)
    
    // Cleanup
    return () => {
      document.removeEventListener('click', handleNoteLinkClick)
    }
  }, [openTab])

  /**
   * Load note data with cache-first strategy and request deduplication
   */
  const loadNote = async () => {
    try {
      setLoading(true)
      console.log('🔍 ViewNotePage: Loading note with ID:', noteId)
      
      // Don't reload if we already have a decrypted note
      if (note && !note.isEncrypted && !isContentEncrypted(note.content)) {
        console.log('🔍 ViewNotePage: Note already decrypted, skipping loadNote')
        setLoading(false)
        return
      }
      
      // Check if we have a decrypted note in hot cache first
      const hotCachedDecryptedNote = getHotCachedNote(noteId)
      if (hotCachedDecryptedNote && !hotCachedDecryptedNote.isEncrypted && !isContentEncrypted(hotCachedDecryptedNote.content)) {
        console.log('🔍 ViewNotePage: Note already decrypted in hot cache, using it and skipping loadNote')
        setNoteSafely(hotCachedDecryptedNote)
        setLoading(false)
        
        // Update tab title if in tab mode
        if (isInTab && tabId && hotCachedDecryptedNote.title) {
          updateTab(tabId, { title: hotCachedDecryptedNote.title })
        }
        return
      }
              const listMeta = notes.find(n => n.noteId === noteId)
      const listLastModified = listMeta ? (listMeta.lastModified || listMeta.cloudModified || listMeta.modifiedTime || listMeta.updatedAt || listMeta.createdAt) : null
      
      // If in local mode, get note from local storage
      if (isLocalMode) {
        console.log('🔍 ViewNotePage: Loading note from local storage')
        const localNote = await getLocalNote(noteId)
        
        if (localNote) {
          console.log('✅ ViewNotePage: Note loaded from local storage:', localNote)
          
          // Check if this is an encrypted sensitive note
          if (localNote.isEncrypted && localNote.content) {
            console.log('🔐 ViewNotePage: Note is encrypted, prompting for password')
            
            // Check if we already have a decrypted version in hot cache
            const decryptedCachedNote2 = getHotCachedNote(noteId)
            if (decryptedCachedNote2 && !decryptedCachedNote2.isEncrypted && !isContentEncrypted(decryptedCachedNote2.content)) {
              console.log('🔐 ViewNotePage: Using decrypted note from hot cache instead of encrypted local note')
              setNoteSafely(decryptedCachedNote2)
              setLoading(false)
              
              // Update tab title if in tab mode
              if (isInTab && tabId && decryptedCachedNote2.title) {
                updateTab(tabId, { title: decryptedCachedNote2.title })
              }
              return
            }
            
            // Always prompt for password for sensitive notes, regardless of session state
            setPendingNote(localNote)
            setNoteSafely(localNote) // Set the note so the component doesn't return early
            setLoading(false)
            // Prompt for password using browser popup
            promptForPassword(localNote)
            return
          } else {
            // Regular note, set as-is
            setNoteSafely(localNote)
          }
          
          // Update tab title if in tab mode
          if (isInTab && tabId && localNote.title) {
            updateTab(tabId, { title: localNote.title })
          }
        } else {
          console.error('❌ ViewNotePage: Note not found in local storage:', noteId)
          toast.error('Note not found')
          
          if (isInTab && tabId) {
            closeTab(tabId)
          } else {
            navigate('/notes')
          }
        }
        
        setLoading(false)
        return
      }
      
      // Cloud mode: Implement cache-first loading with request deduplication
      
      // 0. Check if this is an optimistic/temp note (serve from memory, don't fetch from API)
      if (noteId.startsWith('temp-') || noteId.startsWith('offline-')) {
        console.log('⚡ ViewNotePage: Optimistic note detected, serving from memory')
                  const optimisticNote = notes.find(note => 
            note.noteId === noteId
          )
        
        if (optimisticNote) {
          setNoteSafely(optimisticNote)
          addToHotCache(optimisticNote) // Add to hot cache
          setLoading(false)
          
          // Update tab title if in tab mode
          if (isInTab && tabId && optimisticNote.title) {
            updateTab(tabId, { title: optimisticNote.title })
          }
          return
        } else {
          console.warn('⚠️ ViewNotePage: Optimistic note not found in context state')
          toast.error('Note not found')
          if (isInTab && tabId) {
            closeTab(tabId)
          } else {
            navigate('/notes')
          }
          setLoading(false)
          return
        }
      }
      
      // 1. Check if request is already pending (prevent duplicate API calls)
      if (pendingRequests.current.has(noteId)) {
        console.log('🔄 ViewNotePage: Request already pending, waiting for completion')
        await pendingRequests.current.get(noteId)
        return
      }
      
      // 2. Check if this is a sensitive note first (before hot cache)
      const listNote = notes.find(n => n.noteId === noteId)
      const isSensitiveNote = listNote?.folderId && folders.find(f => f.folderId === listNote.folderId)?.folderType === 'SENSITIVE'
      
      if (isSensitiveNote) {
        console.log('🔐 ViewNotePage: Sensitive note detected, checking if already decrypted...')
        
        // Check if we already have a decrypted note in state
        if (note && !note.isEncrypted && !isContentEncrypted(note.content)) {
          console.log('🔐 ViewNotePage: Note already decrypted in state, skipping password prompt')
          setLoading(false)
          return
        }
        
        // Check if we already have a decrypted note in hot cache
        const hotCachedNote = getHotCachedNote(noteId)
        if (hotCachedNote && !hotCachedNote.isEncrypted && !isContentEncrypted(hotCachedNote.content)) {
          console.log('🔐 ViewNotePage: Note already decrypted in hot cache, using it')
          setNoteSafely(hotCachedNote)
          setLoading(false)
          
          // Update tab title if in tab mode
          if (isInTab && tabId && hotCachedNote.title) {
            updateTab(tabId, { title: hotCachedNote.title })
          }
          return
        }
        
        // For sensitive notes, NEVER use global cache - always require password
        // Global cache should only be used for non-sensitive notes
        console.log('🔐 ViewNotePage: Sensitive note detected - password required, not using global cache')
        
        // Clear any previously cached decrypted content for security
        clearSensitiveNotesFromGlobalCache()
        
        // For sensitive notes, check if already decrypted first
        const cachedNote = await getCachedNoteById(noteId)
        if (cachedNote) {
          // Check if note is already decrypted (not encrypted content)
          if (!cachedNote.isEncrypted && !isContentEncrypted(cachedNote.content)) {
            console.log('🔐 ViewNotePage: Sensitive note already decrypted, proceeding normally')
            const decryptedCachedNote = await decryptNoteIfNeeded(cachedNote)
            setNoteSafely(decryptedCachedNote)
            addToHotCache(decryptedCachedNote)
            setLoading(false)
            
            // Update tab title if in tab mode
            if (isInTab && tabId && cachedNote.title) {
              updateTab(tabId, { title: cachedNote.title })
            }
            return
          }
          
          setPendingNote(cachedNote)
          setNoteSafely(cachedNote)
          setLoading(false)
          console.log('🔐 ViewNotePage: Prompting for password for sensitive note')
          // Prompt for password using browser popup
          promptForPassword(cachedNote)
          return
        } else {
          console.error('❌ ViewNotePage: Sensitive note not found in cache:', noteId)
          toast.error('Sensitive note not found')
          setLoading(false)
          return
        }
      }
      
      // 3. Check hot memory cache (instant access) - for non-sensitive notes
      const hotCachedNote = getHotCachedNote(noteId)
      if (hotCachedNote) {
        // Don't override a decrypted note with an encrypted one
        if (note && !note.isEncrypted && !isContentEncrypted(note.content) && 
            hotCachedNote.isEncrypted && isContentEncrypted(hotCachedNote.content)) {
          console.log('🔐 ViewNotePage: Skipping hot cache - current note is already decrypted')
          setLoading(false)
          return
        }
        
        // Don't override a decrypted note with another decrypted note if we already have one
        if (note && !note.isEncrypted && !isContentEncrypted(note.content) && 
            !hotCachedNote.isEncrypted && !isContentEncrypted(hotCachedNote.content)) {
          console.log('🔐 ViewNotePage: Skipping hot cache - current note is already decrypted and same as cached')
          setLoading(false)
          return
        }
        
        setNoteSafely(hotCachedNote)
        setLoading(false)
        
        // Update tab title if in tab mode
        if (isInTab && tabId && hotCachedNote.title) {
          updateTab(tabId, { title: hotCachedNote.title })
        }
        // Background refresh if list shows newer metadata
        backgroundRefreshNote(noteId, listLastModified)
        return
      }
      
      // 4. Check IndexedDB cache (fast but not instant) - for non-sensitive notes
      console.log('💾 ViewNotePage: Checking IndexedDB cache...')
      const cachedNote = await getCachedNoteById(noteId)
      if (cachedNote) {
        console.log('✅ ViewNotePage: Note loaded from IndexedDB cache')
        
        // Check if this is a sensitive note that needs authentication
        console.log('🔍 ViewNotePage: Checking cached note for sensitive folder:', {
          folderId: cachedNote.folderId,
          isEncrypted: cachedNote.isEncrypted,
          hasContent: !!cachedNote.content,
          contentLength: cachedNote.content?.length,
          contentAppearsEncrypted: isContentEncrypted(cachedNote.content)
        })
        
        if (cachedNote.folderId) {
          const noteFolder = folders.find(f => f.folderId === cachedNote.folderId)
          console.log('🔍 ViewNotePage: Found folder:', {
            folderId: noteFolder?.folderId,
            folderType: noteFolder?.folderType,
            folderName: noteFolder?.name
          })
          
          if (noteFolder?.folderType === 'SENSITIVE' && (cachedNote.isEncrypted || isContentEncrypted(cachedNote.content)) && cachedNote.content) {
            // Always prompt for password for sensitive notes, regardless of session state
            console.log('🔐 ViewNotePage: Sensitive note detected, prompting for password')
            setPendingNote(cachedNote)
            setNoteSafely(cachedNote) // Set the note so the component doesn't return early
            setLoading(false)
            // Prompt for password using browser popup
            promptForPassword(cachedNote)
            return
          } else {
            // Regular note or non-encrypted sensitive note
            console.log('🔍 ViewNotePage: Note is not encrypted sensitive note, proceeding with normal decryption')
            const decryptedCachedNote = await decryptNoteIfNeeded(cachedNote)
            setNoteSafely(decryptedCachedNote)
            addToHotCache(decryptedCachedNote) // Add to hot cache for next time
          }
        } else {
          // Regular note
          const decryptedCachedNote = await decryptNoteIfNeeded(cachedNote)
          setNoteSafely(decryptedCachedNote)
          addToHotCache(decryptedCachedNote) // Add to hot cache for next time
          
          // Pre-cache images for offline use
          if (decryptedCachedNote.content) {
            preCacheImagesFromContent(decryptedCachedNote.content).catch(error => {
              console.warn('⚠️ ViewNotePage: Failed to pre-cache images from cached note:', error)
            })
          }
        }
        
        setLoading(false)
        
        // Update tab title if in tab mode
        if (isInTab && tabId && cachedNote.title) {
          updateTab(tabId, { title: cachedNote.title })
        }
        
        // Background refresh if list shows newer metadata than cache
        backgroundRefreshNote(noteId, listLastModified)
        
        return
      }
      
      // 4. Cache miss - use frontend sync if enabled, otherwise fetch from backend API
      let apiRequest
      if (SYNC_CONFIG.USE_FRONTEND_SYNC) {
        console.log('🌐 ViewNotePage: Cache miss, using frontend sync to load note by ID...')
        const TIMEOUT_MS = 10000
        const loadPromise = frontendSync.loadNoteById(noteId, { minLastModified: listLastModified || null })
        const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('LOAD_TIMEOUT')), TIMEOUT_MS))
        apiRequest = Promise.race([loadPromise, timeoutPromise])
      } else {
        console.log('🌐 ViewNotePage: Cache miss, fetching from API...')
        apiRequest = fetchNoteFromAPI(noteId)
      }
      pendingRequests.current.set(noteId, apiRequest)
      
      try {
        const response = await apiRequest
        
        if (response) {
          // Decrypt the note if needed before setting state
          const decryptedResponse = await decryptNoteIfNeeded(response)
          
          setNoteSafely(decryptedResponse)
          addToHotCache(decryptedResponse) // Add to hot cache
          
          // Pre-cache images for offline use
          if (decryptedResponse.content) {
            preCacheImagesFromContent(decryptedResponse.content).catch(error => {
              console.warn('⚠️ ViewNotePage: Failed to pre-cache images:', error)
            })
          }
          
          // Cache the encrypted version (not decrypted) for consistency
          await cacheSingleNote(response)
          
          // Update tab title if in tab mode
          if (isInTab && tabId && decryptedResponse.title) {
            updateTab(tabId, { title: decryptedResponse.title })
          }
        }
      } catch (e) {
        if (e && e.message === 'LOAD_TIMEOUT') {
          console.warn('⏳ ViewNotePage: Timed out loading note content; showing fallback message')
          setNoteSafely({ noteId, title: 'Large note', content: 'This note is large and is still loading from cloud. Please wait or click refresh.' })
          // Kick a background load without blocking UI
          frontendSync.loadNoteById(noteId, { forceRefresh: true }).then(async (fresh) => {
            if (fresh) {
              // Decrypt the fresh note before setting state
              const decryptedFresh = await decryptNoteIfNeeded(fresh)
              
              setNoteSafely(decryptedFresh)
              addToHotCache(decryptedFresh)
              
              // Pre-cache images for offline use
              if (decryptedFresh.content) {
                preCacheImagesFromContent(decryptedFresh.content).catch(error => {
                  console.warn('⚠️ ViewNotePage: Failed to pre-cache images from timeout recovery:', error)
                })
              }
              
              // Cache the encrypted version (not decrypted) for consistency
              cacheSingleNote(fresh).catch(() => {})
            }
          }).catch(() => {})
        } else {
          throw e
        }
      } finally {
        pendingRequests.current.delete(noteId)
      }

    } catch (error) {
      console.error('❌ ViewNotePage: Error loading note:', error)
      toast.error('Failed to load note')
      
      if (isInTab && tabId) {
        closeTab(tabId)
      } else {
        navigate('/notes')
      }
    } finally {
      setLoading(false)
    }
  }

  /**
   * Decrypt note content if needed (for sensitive notes)
   */
  const decryptNoteIfNeeded = async (noteData) => {
    if (!noteData.folderId) return noteData
    
    // Check if note is in a sensitive folder
    const noteFolder = folders.find(f => f.folderId === noteData.folderId)
    
    // If this is a sensitive folder note, don't automatically decrypt it
    // The sensitive note detection logic will handle it separately
    if (noteFolder?.folderType === 'SENSITIVE') {
      console.log('🔐 ViewNotePage: Sensitive folder note detected, returning as-is for separate handling')
      return noteData
    }
    
    // For non-sensitive notes, check if they're encrypted and decrypt if needed
    const isEncrypted = noteData.isEncrypted
    
    if (!isEncrypted || !noteData.content) return noteData
    
    // For non-sensitive encrypted notes, try to decrypt if we have a session
    if (sensitiveSession.isAuthenticated) {
      const password = isLocalMode ? getSensitivePassword() : sensitiveSession.sessionPassword
      
      if (password) {
        try {
          const decryptedContent = localStorageService.decryptContent(noteData.content, password)
          console.log('✅ ViewNotePage: Non-sensitive note decrypted successfully')
          return { ...noteData, content: decryptedContent, isEncrypted: false }
        } catch (error) {
          console.error('❌ ViewNotePage: Failed to decrypt non-sensitive note:', error)
          return {
            ...noteData,
            content: '🔒 Failed to decrypt this note. Please try again.'
          }
        }
      }
    }
    
    // If we can't decrypt, return as-is
    return noteData
  }

  /**
   * Fetch note from API (extracted for reusability)
   */
  const fetchNoteFromAPI = async (noteId) => {
    // For cloud mode, try frontend sync first (handles sensitive notes properly)
    if (shouldUseFrontendSync('read')) {
      try {
        const response = await frontendSync.loadNoteById(noteId)
        if (response) {
          console.log('✅ ViewNotePage: Note loaded via frontend sync:', response)
          
          // Decrypt if needed
          const decryptedResponse = await decryptNoteIfNeeded(response)
          
          // Cache the encrypted version (not decrypted) for consistency
          await cacheSingleNote(response)
          
          return decryptedResponse
        }
      } catch (error) {
        console.log('⚠️ ViewNotePage: Frontend sync failed, falling back to API:', error.message)
      }
    }
    
    // Fallback to regular API
    const response = await notesAPI.getNoteById(noteId)
    console.log('✅ ViewNotePage: Note loaded from API:', response)
    
    if (response) {
      // Decrypt if needed
      const decryptedResponse = await decryptNoteIfNeeded(response)
      return decryptedResponse
    }
     
     return null
   }

  /**
   * Handle note deletion
   */
  const handleDelete = async () => {
    if (!note) return

    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${note.title || 'this note'}"? This action cannot be undone.`
    )
    
    if (!confirmDelete) return

    try {
      setIsDeleting(true)
      console.log('🔍 ViewNotePage: Deleting note:', noteId)
      
      await deleteNote(noteId)
      
      if (isInTab && tabId) {
        closeTab(tabId)
      } else {
        navigate('/notes', { replace: true })
      }
    } catch (error) {
      console.error('❌ ViewNotePage: Error deleting note:', error)
      
      if (error.response?.status === 401) {
        toast.error('Please log in to delete notes')
        navigate('/login')
      } else if (error.response?.status === 404) {
        toast.error('Note not found')
      } else {
        toast.error('Failed to delete note. Please try again.')
      }
    } finally {
      setIsDeleting(false)
    }
  }

  /**
   * Handle note editing
   */
  const handleEdit = () => {
    if (!note) return
    
    if (isInTab) {
      // Open edit tab
      openTab({
        type: 'edit',
        noteId: noteId,
        title: note.title || 'Untitled Note',
        note: note
      })
    } else {
      // Navigate to edit page
      navigate(`/notes/${noteId}/edit`)
    }
  }

  /**
   * Handle note export
   */
  const handleExport = () => {
    if (!note) return

    try {
      // Create export content with note metadata
      const exportContent = `# ${note.title || 'Untitled Note'}

**Created:** ${formatDate(note.createdAt)}
${note.lastModified && note.lastModified !== note.createdAt ? `**Last Modified:** ${formatDate(note.lastModified)}\n` : ''}
**Characters:** ${contentStats.characters}
**Words:** ${contentStats.words}

---

${note.content || 'No content available.'}`

      // Create and download file
      const blob = new Blob([exportContent], { type: 'text/markdown' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${(note.title || 'Untitled Note').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.md`
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      
      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      }, 100)
      
      toast.success('Note exported successfully')
    } catch (error) {
      console.error('❌ ViewNotePage: Error exporting note:', error)
      toast.error('Failed to export note')
    }
  }


  /**
   * Prompt for password using browser popup
   */
  const promptForPassword = async (noteToDecrypt) => {
    const noteId = noteToDecrypt.noteId || noteToDecrypt.id
    const cacheKey = `password-prompt-${noteId}`
    
    // Check if password prompt is already in progress for this note
    if (globalPasswordPromptCache.has(cacheKey)) {
      console.log('🔐 ViewNotePage: Password prompt already in progress for this note, skipping duplicate')
      return
    }
    
    // Mark password prompt as in progress
    globalPasswordPromptCache.set(cacheKey, { inProgress: true, timestamp: Date.now() })
    
    try {
      const noteTitle = noteToDecrypt.title || 'this note'
      const password = window.prompt(
        `This note "${noteTitle}" is encrypted and requires your password to access.\n\nEnter your password:`
      )
      
      if (password === null) {
        // User cancelled
        console.log('🔐 ViewNotePage: User cancelled password prompt')
        globalPasswordPromptCache.delete(cacheKey)
        handlePasswordCancel()
        return
      }
      
      if (!password.trim()) {
        // Empty password
        toast.error('Password is required')
        // Retry the prompt
        setTimeout(() => {
          globalPasswordPromptCache.delete(cacheKey)
          promptForPassword(noteToDecrypt)
        }, 100)
        return
      }
      
      try {
        await handlePasswordConfirm(password, noteToDecrypt)
        // Clear the cache on successful authentication
        globalPasswordPromptCache.delete(cacheKey)
      } catch (error) {
        console.error('❌ ViewNotePage: Password authentication failed:', error)
        toast.error(error.message || 'Invalid password. Please try again.')
        // Retry the prompt
        setTimeout(() => {
          globalPasswordPromptCache.delete(cacheKey)
          promptForPassword(noteToDecrypt)
        }, 100)
      }
    } catch (error) {
      // Clear the cache on any error
      globalPasswordPromptCache.delete(cacheKey)
      throw error
    }
  }

  /**
   * Handle password confirmation for sensitive note access
   */
  const handlePasswordConfirm = async (password, noteToDecrypt = null) => {
    console.log('🔐 ViewNotePage: Password confirmation handler called with password length:', password?.length)
    
    const noteData = noteToDecrypt || pendingNote
    if (!noteData) {
      throw new Error('No note available for decryption')
    }
    
    // Set decryption flag to prevent race conditions
    isDecrypting.current = true
    console.log('🔍 ViewNotePage: Setting isDecrypting to true')
    
    try {
      // Try to load from SensitiveNotesService first
      try {
        const { sensitiveNotesService } = await import('../../services/sensitiveNotesService')
        const sensitiveNote = await sensitiveNotesService.getNote(noteData.noteId || noteData.id, password)
        
        if (sensitiveNote) {
          console.log('✅ ViewNotePage: Note loaded from SensitiveNotesService')
          console.log('🔍 ViewNotePage: Full decrypted note object:', sensitiveNote)
          console.log('🔍 ViewNotePage: Content property:', sensitiveNote.content)
          console.log('🔍 ViewNotePage: Content type:', typeof sensitiveNote.content)
          console.log('🔍 ViewNotePage: Setting note state with decrypted content:', sensitiveNote.content?.substring(0, 50))
          setNoteSafely(sensitiveNote)
          addToHotCache(sensitiveNote)
          console.log('🔍 ViewNotePage: Note state set, current note state should now have decrypted content')
          
          // Force re-render by updating a render key
          setRenderKey(prev => {
            const newKey = prev + 1
            console.log('🔄 ViewNotePage: Updating renderKey from', prev, 'to', newKey)
            return newKey
          })
          
          // Update tab title if in tab mode
          if (isInTab && tabId && sensitiveNote.title) {
            updateTab(tabId, { title: sensitiveNote.title })
          }
          
          // Clear pending note
          setPendingNote(null)
          return
        }
      } catch (error) {
        console.log('🔐 ViewNotePage: Note not found in SensitiveNotesService, migrating from cache...')
      }
      
      // If not found in SensitiveNotesService, migrate from cache
      try {
        const { sensitiveNotesService } = await import('../../services/sensitiveNotesService')
        
        console.log('🔐 ViewNotePage: Migrating note to SensitiveNotesService with provided password')
        
        // Use the proper migration method that decrypts from old system and re-encrypts with new system
        const migratedNote = await sensitiveNotesService.migrateNote(noteData, password)
        console.log('✅ ViewNotePage: Note migrated to SensitiveNotesService')
        
        // Update local storage with the migrated note to prevent re-loading old encrypted data
        if (isLocalMode) {
          try {
            console.log('🔐 ViewNotePage: Updating local storage with migrated note')
            await updateLocalNote(noteData.noteId || noteData.id, {
              ...migratedNote,
              lastModified: new Date().toISOString()
            })
            console.log('✅ ViewNotePage: Local storage updated with migrated note')
          } catch (error) {
            console.warn('⚠️ ViewNotePage: Failed to update local storage:', error)
          }
        }
        
        setNoteSafely(migratedNote)
        addToHotCache(migratedNote)
        
        // Force re-render by updating a render key
        setRenderKey(prev => {
          const newKey = prev + 1
          console.log('🔄 ViewNotePage: Updating renderKey from', prev, 'to', newKey, '(migration)')
          return newKey
        })
        
        // Update tab title if in tab mode
        if (isInTab && tabId && migratedNote.title) {
          updateTab(tabId, { title: migratedNote.title })
        }
        
        // Clear pending note
        setPendingNote(null)
        return
        
      } catch (migrationError) {
        console.error('❌ ViewNotePage: Failed to migrate note:', migrationError)
        throw new Error('Failed to decrypt note. Please check your password.')
      }
      
    } catch (error) {
      console.error('❌ ViewNotePage: Password decryption failed:', error)
      throw error
    } finally {
      // Clear decryption flag
      isDecrypting.current = false
      console.log('🔍 ViewNotePage: Setting isDecrypting to false')
    }
  }

  /**
   * Handle password cancellation
   */
  const handlePasswordCancel = () => {
    console.log('🔐 ViewNotePage: Password cancelled')
    setPendingNote(null)
    // Navigate back to notes page if not in tab mode
    if (!isInTab) {
      navigate('/notes')
    } else if (tabId) {
      // Close the tab if in tab mode
      closeTab(tabId)
    }
  }

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown'
    
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  /**
   * Process attachment links in rendered content
   */
  const processAttachmentLinks = (containerRef) => {
    console.log('ViewNotePage: processAttachmentLinks called')
    if (!containerRef.current) {
      console.log('ViewNotePage: No containerRef.current')
      return
    }

    // Look for both old-style hash URLs and new-style javascript:void(0) links
    const attachmentLinks = containerRef.current.querySelectorAll('a[href^="#attachment-"], a[href^="#local-attachment-"], a[href^="#cloud-attachment-"], a[href="javascript:void(0)"], a[href="about:blank"]')
    console.log('ViewNotePage: Found attachment links:', attachmentLinks.length)
    
    attachmentLinks.forEach((link, index) => {
      // Use a unique attribute to ensure links are processed correctly
      const processedAttr = 'data-view-processed'
      
      if (link.hasAttribute(processedAttr)) {
        console.log(`ViewNotePage: Link ${index} already processed`)
        return
      }
      
      console.log(`ViewNotePage: Processing link ${index}`)

      const href = link.getAttribute('href')
      const fileId = link.getAttribute('data-file-id')
      const fileName = link.getAttribute('data-file-name') || link.textContent.replace('📎 ', '')
      const originalFileName = link.getAttribute('data-original-file-name')
      const isLocalFile = link.getAttribute('data-local-file') === 'true'
      const isCloudFile = link.getAttribute('data-cloud-file') === 'true'
      
      // Check if this is a new-style attachment link
      const isNewStyleLink = href === 'javascript:void(0)' || href === 'about:blank'
      
      // For old-style links, extract fileId from href
      let extractedFileId = fileId
      if (!fileId && href) {
        if (href.startsWith('#local-attachment-')) {
          extractedFileId = href.replace('#local-attachment-', '')
        } else if (href.startsWith('#cloud-attachment-')) {
          extractedFileId = href.replace('#cloud-attachment-', '')
        } else if (href.startsWith('#attachment-')) {
          extractedFileId = href.replace('#attachment-', '')
        }
      }
      
      // If still no fileId and this is a new-style link, we need to search for it
      if (!extractedFileId && isNewStyleLink) {
        console.log('ViewNotePage: New-style link without fileId, will search by name:', fileName)
        // We'll handle this in the click handler
      }
      
      console.log(`ViewNotePage: Processing link ${index}:`, { 
        href, 
        fileId: extractedFileId, 
        fileName, 
        originalFileName,
        isLocalFile, 
        isCloudFile, 
        isNewStyleLink,
        linkHTML: link.outerHTML.substring(0, 200) + '...'
      })
      
      // Create download URL based on file type
      let downloadUrl
      if (isLocalFile || (href && href.startsWith('#local-attachment-'))) {
        downloadUrl = `local://${extractedFileId}`
      } else if (isCloudFile || (href && href.startsWith('#cloud-attachment-'))) {
        downloadUrl = `gdrive://${extractedFileId}`
      } else {
        const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
        downloadUrl = `${API_BASE_URL}/api/files/${extractedFileId}`
      }
      
      // Remove old event listeners by cloning the element
      const newLink = link.cloneNode(true)
      link.parentNode.replaceChild(newLink, link)
      
      // Add styling and attributes
      newLink.title = `Download ${fileName}`
      if (extractedFileId) {
        newLink.setAttribute('data-file-id', extractedFileId)
      }
      newLink.setAttribute('data-file-name', fileName)
      if (originalFileName) {
        newLink.setAttribute('data-original-file-name', originalFileName)
      }
      newLink.setAttribute('data-download-url', downloadUrl)
      newLink.setAttribute(processedAttr, 'true')
      if (isLocalFile || (href && href.startsWith('#local-attachment-'))) {
        newLink.setAttribute('data-local-file', 'true')
      } else if (isCloudFile || (href && href.startsWith('#cloud-attachment-')) || isNewStyleLink) {
        newLink.setAttribute('data-cloud-file', 'true')
      }
      
      // Add click handler
      newLink.addEventListener('click', async (e) => {
        console.log('ViewNotePage: Attachment link clicked:', fileName)
        e.preventDefault()
        e.stopPropagation()
        
        // Get current link attributes (in case they changed)
        const currentHref = newLink.getAttribute('href')
        const currentFileIdRaw = newLink.getAttribute('data-file-id')
        const currentFileId = currentFileIdRaw === 'null' ? null : currentFileIdRaw
        const currentFileName = newLink.getAttribute('data-file-name') || fileName
        const currentIsLocalFile = newLink.getAttribute('data-local-file') === 'true'
        const currentIsCloudFile = newLink.getAttribute('data-cloud-file') === 'true'
        const currentIsNewStyleLink = currentHref === 'javascript:void(0)' || currentHref === 'about:blank'
        
        console.log('ViewNotePage: Current link attributes:', {
          href: currentHref,
          fileId: currentFileId,
          fileName: currentFileName,
          isLocalFile: currentIsLocalFile,
          isCloudFile: currentIsCloudFile,
          isNewStyleLink: currentIsNewStyleLink
        })
        
        // Show floating spinner for file operations (declared outside try-catch)
        const spinnerToast = toast.loading(
          `Opening ${currentFileName}...`, 
          {
            duration: Infinity, // Don't auto-dismiss
            style: {
              background: 'rgba(31, 41, 55, 0.5)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              color: 'white',
              borderRadius: '12px',
              padding: '12px 16px',
              fontSize: '14px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }
          }
        )
        
        try {
          
          if ((currentIsLocalFile || (currentHref && currentHref.startsWith('#local-attachment-'))) && isLocalMode) {
            // Handle local file download
            console.log('ViewNotePage: Downloading local file:', currentFileId)
            
            if (!getLocalFile) {
              throw new Error('Local file operations not available')
            }
            
            const localFile = await getLocalFile(currentFileId)
            
            const url = window.URL.createObjectURL(localFile.blob)
            const a = document.createElement('a')
            a.href = url
            a.download = localFile.fileName
            a.style.display = 'none'
            document.body.appendChild(a)
            a.click()
            setTimeout(() => {
              window.URL.revokeObjectURL(url)
              document.body.removeChild(a)
            }, 100)
            
            // Dismiss spinner and show success
            toast.dismiss(spinnerToast)
            toast.success(`File "${localFile.fileName}" downloaded successfully!`, {
              duration: 2000,
              style: {
                background: 'rgba(16, 185, 129, 0.5)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                color: 'white',
                borderRadius: '12px',
                padding: '12px 16px',
                fontSize: '14px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                border: '1px solid rgba(16, 185, 129, 0.3)'
              }
            })
            
          } else if (currentIsCloudFile || (currentHref && currentHref.startsWith('#cloud-attachment-')) || currentIsNewStyleLink) {
            // Handle Google Drive file download
            console.log('ViewNotePage: Downloading Google Drive file:', currentFileId)
            
            if (!window.googleDriveAPI || !window.googleDriveAPI.accessToken) {
              throw new Error('Google Drive API not available or not authenticated')
            }
            
        // First, always try to get from local cache (for offline access)
        try {
          // Clean the filename by removing emoji and extra characters
          const cleanFileName = currentFileName.replace(/^[🎵📎🎨📷📄🎬]+\s*/, '').trim()
          console.log('ViewNotePage: Checking local cache for file:', currentFileName, '-> cleaned:', cleanFileName)
          
          const cachedAttachment = await localStorageService.getAttachment(cleanFileName)
          if (cachedAttachment) {
            console.log('ViewNotePage: Found cached attachment:', cleanFileName)
            
            // Convert base64 back to blob
            const binaryString = atob(cachedAttachment.data)
            const bytes = new Uint8Array(binaryString.length)
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i)
            }
            const blob = new Blob([bytes], { type: cachedAttachment.fileType })
            
            // Handle MIME types for in-browser opening vs downloading
            const url = window.URL.createObjectURL(blob)
            let mimeType = cachedAttachment.fileType
            const fileName = cachedAttachment.fileName || cleanFileName
            
            // Files that should open in browser
            const browserOpenableTypes = [
              'text/plain',
              'text/markdown', 
              'text/html',
              'text/css',
              'text/javascript',
              'application/json',
              'application/pdf',
              'image/jpeg',
              'image/png',
              'image/gif',
              'image/webp',
              'image/svg+xml',
              'video/mp4',
              'video/webm',
              'audio/mp3',
              'audio/wav',
              'audio/webm',
              'audio/ogg',
              'audio/mpeg',
              'application/xml',
              'text/csv',
              'text/xml',
              'application/javascript',
              'text/javascript'
            ]
            
            console.log('ViewNotePage: Cached file MIME type:', mimeType, 'for file:', fileName)
            
            // Check if MIME type is browser-openable (handle codec specifications like "audio/webm;codecs=opus")
            const baseMimeType = mimeType ? mimeType.split(';')[0].trim() : ''
            const isBrowserOpenable = browserOpenableTypes.includes(mimeType) || 
                                     browserOpenableTypes.includes(baseMimeType) ||
                                     (baseMimeType.startsWith('audio/') && browserOpenableTypes.some(type => type.startsWith('audio/')))
            
            console.log('ViewNotePage: Cached MIME type check:', { mimeType, baseMimeType, isBrowserOpenable })
            
            if (isBrowserOpenable) {
              // Open in new tab
              console.log('ViewNotePage: Opening cached file in browser:', fileName)
              const newTab = window.open(url, '_blank')
              if (!newTab) {
                // Fallback if popup blocked
                console.log('ViewNotePage: Popup blocked, using fallback method')
                const a = document.createElement('a')
                a.href = url
                a.target = '_blank'
                a.click()
              }
            } else {
              // Force download for other file types
              console.log('ViewNotePage: Downloading cached file:', fileName)
              const a = document.createElement('a')
              a.href = url
              a.download = fileName
              a.style.display = 'none'
              document.body.appendChild(a)
              a.click()
              document.body.removeChild(a)
            }
            
            // Clean up after a delay
            setTimeout(() => {
              window.URL.revokeObjectURL(url)
            }, 1000)
            
            // Dismiss spinner and show success
            toast.dismiss(spinnerToast)
            toast.success(`File "${fileName}" opened from cache!`, {
              duration: 2000,
              style: {
                background: 'rgba(16, 185, 129, 0.5)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                color: 'white',
                borderRadius: '12px',
                padding: '12px 16px',
                fontSize: '14px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                border: '1px solid rgba(16, 185, 129, 0.3)'
              }
            })
            console.log('ViewNotePage: Cached file processed successfully:', fileName)
            return
          }
        } catch (cacheError) {
          console.log('ViewNotePage: No cached attachment found, proceeding with Google Drive:', cacheError.message)
        }
        
        // Use the same download logic as RichTextEditor
        let actualFileId = currentFileId
        
        // If no fileId or it's a temp ID, search for the file by name
        if (!actualFileId || actualFileId === 'null' || actualFileId === null || actualFileId.startsWith('temp-')) {
          // Clean the filename by removing emoji and extra characters
          const cleanFileName = currentFileName.replace(/^[🎵📎🎨📷📄🎬]+\s*/, '').trim()
          console.log('ViewNotePage: Searching for file by name in Google Drive:', currentFileName, '-> cleaned:', cleanFileName)
          
          // Search for the file by name (use cleaned filename)
          const searchQuery = `name='${cleanFileName}' and trashed=false`
              const searchResponse = await window.googleDriveAPI.apiRequest(`${window.googleDriveAPI.API_BASE}/files`, {
                q: searchQuery,
                fields: 'files(id,name,mimeType)'
              })
              
              const searchData = searchResponse.files ? searchResponse : await searchResponse.json()
              
              if (searchData.files && searchData.files.length > 0) {
                actualFileId = searchData.files[0].id
                console.log('ViewNotePage: Found file by search:', actualFileId)
              } else {
                // Fallback: search recent files with flexible matching
                console.log('ViewNotePage: Direct search failed, trying recent files with flexible matching')
                const recentFiles = await window.googleDriveAPI.apiRequest(`${window.googleDriveAPI.API_BASE}/files`, {
                  q: 'trashed=false',
                  fields: 'files(id,name,mimeType)',
                  orderBy: 'modifiedTime desc',
                  pageSize: 100
                })
                
                const recentData = recentFiles.files ? recentFiles : await recentFiles.json()
                console.log('ViewNotePage: Recent files sample:', recentData.files?.slice(0, 5).map(f => f.name))
                
                // Try exact match first (use cleaned filename)
                let matchingFile = recentData.files?.find(file => file.name === cleanFileName)
                
                if (matchingFile) {
                  console.log('ViewNotePage: Found exact filename match:', matchingFile.name)
                } else {
                  // Try case-insensitive match
                  console.log('ViewNotePage: No exact match, trying case-insensitive search')
                  matchingFile = recentData.files?.find(file => file.name.toLowerCase() === cleanFileName.toLowerCase())
                  
                  if (matchingFile) {
                    console.log('ViewNotePage: Found case-insensitive match:', matchingFile.name)
                  }
                }
                
                // If still no match, try partial match
                if (!matchingFile) {
                  console.log('ViewNotePage: No case-insensitive match, trying partial search')
                  const fileNameWithoutExt = cleanFileName.split('.')[0]
                  matchingFile = recentData.files?.find(file =>
                    file.name.toLowerCase().includes(fileNameWithoutExt.toLowerCase()) ||
                    fileNameWithoutExt.toLowerCase().includes(file.name.split('.')[0].toLowerCase())
                  )
                }

                // If still no match, try searching for files that start with the base filename (for timestamped files)
                if (!matchingFile) {
                  console.log('ViewNotePage: No partial match, trying timestamped file search')
                  const baseFileName = currentFileName.replace(/\.[^/.]+$/, '') // Remove extension
                  const timestampedFiles = recentData.files?.filter(file => {
                    const fileBaseName = file.name.replace(/\.[^/.]+$/, '') // Remove extension
                    return fileBaseName.toLowerCase().startsWith(baseFileName.toLowerCase() + '_')
                  })
                  
                  if (timestampedFiles && timestampedFiles.length > 0) {
                    console.log(`ViewNotePage: Found ${timestampedFiles.length} timestamped files with base name '${baseFileName}':`, timestampedFiles.map(f => f.name))
                    
                    // If multiple files found, get the most recent one (files are already sorted by modifiedTime desc)
                    matchingFile = timestampedFiles[0]
                    console.log(`ViewNotePage: Selected most recent file: ${matchingFile.name}`)
                    
                    // Warn user if multiple files exist with same base name
                    if (timestampedFiles.length > 1) {
                      console.warn(`⚠️ ViewNotePage: Multiple files found with base name '${baseFileName}'. Selected the most recent one: ${matchingFile.name}`)
                      console.warn(`⚠️ Available files: ${timestampedFiles.map(f => f.name).join(', ')}`)
                    }
                  }
                }

                // If still no match, try searching for files with old random format (timestamp_random.ext)
                if (!matchingFile) {
                  console.log('ViewNotePage: No timestamped match, trying old random format search')
                  const baseFileName = currentFileName.replace(/\.[^/.]+$/, '') // Remove extension
                  const oldFormatFiles = recentData.files?.filter(file => {
                    // Check if file matches old format: timestamp_random.ext
                    const fileBaseName = file.name.replace(/\.[^/.]+$/, '') // Remove extension
                    const parts = fileBaseName.split('_')
                    if (parts.length === 2) {
                      // Check if first part is a timestamp (all digits) and second part is random string
                      const isTimestamp = /^\d+$/.test(parts[0])
                      const isRandomString = /^[a-z0-9]+$/i.test(parts[1]) && parts[1].length >= 8
                      return isTimestamp && isRandomString
                    }
                    return false
                  })
                  
                  if (oldFormatFiles && oldFormatFiles.length > 0) {
                    console.log(`ViewNotePage: Found ${oldFormatFiles.length} files with old random format:`, oldFormatFiles.map(f => f.name))
                    
                    // For old format files, we can't match by name, so we'll take the most recent one
                    // This is a fallback for files uploaded before the new naming scheme
                    matchingFile = oldFormatFiles[0]
                    console.log(`ViewNotePage: Selected most recent old format file: ${matchingFile.name}`)
                    console.warn(`⚠️ ViewNotePage: Using old format file as fallback. Consider re-uploading for better naming.`)
                  }
                }
                
                if (matchingFile) {
                  actualFileId = matchingFile.id
                  console.log('ViewNotePage: Found file in recent files:', actualFileId, matchingFile.name)
                } else {
                  console.log('ViewNotePage: Available files:', recentData.files?.map(f => f.name).slice(0, 10))
                  throw new Error(`File not found: ${currentFileName}`)
                }
              }
            }
            
            const cloudFile = await window.googleDriveAPI.downloadAttachment(actualFileId)
            
            console.log('ViewNotePage: Download response:', cloudFile)
            console.log('ViewNotePage: Download response keys:', Object.keys(cloudFile || {}))
            console.log('ViewNotePage: Download response values:', {
              blob: cloudFile?.blob,
              fileName: cloudFile?.fileName,
              fileType: cloudFile?.fileType,
              fileSize: cloudFile?.fileSize,
              mimeType: cloudFile?.mimeType
            })
            
            // Handle MIME types for in-browser opening vs downloading
            const url = window.URL.createObjectURL(cloudFile.blob)
            let mimeType = cloudFile.fileType || cloudFile.mimeType
            const fileName = cloudFile.fileName || currentFileName
            
            // Fallback MIME type detection from file extension
            if (!mimeType && fileName) {
              const ext = fileName.split('.').pop().toLowerCase()
              const mimeTypes = {
                'md': 'text/markdown',
                'txt': 'text/plain',
                'html': 'text/html',
                'htm': 'text/html',
                'css': 'text/css',
                'js': 'text/javascript',
                'json': 'application/json',
                'pdf': 'application/pdf',
                'jpg': 'image/jpeg',
                'jpeg': 'image/jpeg',
                'png': 'image/png',
                'gif': 'image/gif',
                'webp': 'image/webp',
                'svg': 'image/svg+xml',
                'mp4': 'video/mp4',
                'webm': 'video/webm',
                'mp3': 'audio/mp3',
                'wav': 'audio/wav',
                'ogg': 'audio/ogg',
                'mpeg': 'audio/mpeg',
                'xml': 'application/xml',
                'csv': 'text/csv'
              }
              mimeType = mimeTypes[ext] || 'application/octet-stream'
              console.log('ViewNotePage: Detected MIME type from extension:', mimeType, 'for extension:', ext)
            }
            
            // Files that should open in browser
            const browserOpenableTypes = [
              'text/plain',
              'text/markdown', 
              'text/html',
              'text/css',
              'text/javascript',
              'application/json',
              'application/pdf',
              'image/jpeg',
              'image/png',
              'image/gif',
              'image/webp',
              'image/svg+xml',
              'video/mp4',
              'video/webm',
              'audio/mp3',
              'audio/wav',
              'audio/webm',
              'audio/ogg',
              'audio/mpeg',
              'application/xml',
              'text/csv',
              'text/xml',
              'application/javascript',
              'text/javascript'
            ]
            
            console.log('ViewNotePage: File MIME type:', mimeType, 'for file:', fileName)
            
            // Check if MIME type is browser-openable (handle codec specifications like "audio/webm;codecs=opus")
            const baseMimeType = mimeType ? mimeType.split(';')[0].trim() : ''
            const isBrowserOpenable = browserOpenableTypes.includes(mimeType) || 
                                     browserOpenableTypes.includes(baseMimeType) ||
                                     (baseMimeType.startsWith('audio/') && browserOpenableTypes.some(type => type.startsWith('audio/')))
            
            console.log('ViewNotePage: MIME type check:', { mimeType, baseMimeType, isBrowserOpenable })
            
            if (isBrowserOpenable) {
              // Open in new tab
              console.log('ViewNotePage: Opening file in browser:', fileName)
              const newTab = window.open(url, '_blank')
              if (!newTab) {
                // Fallback if popup blocked
                console.log('ViewNotePage: Popup blocked, using fallback method')
                const a = document.createElement('a')
                a.href = url
                a.target = '_blank'
                a.click()
              }
            } else {
              // Force download for other file types
              console.log('ViewNotePage: Downloading file:', fileName)
              const a = document.createElement('a')
              a.href = url
              a.download = fileName
              a.style.display = 'none'
              document.body.appendChild(a)
              a.click()
              document.body.removeChild(a)
            }
            
            // Clean up after a delay
            setTimeout(() => {
              window.URL.revokeObjectURL(url)
            }, 1000)
            
            // Dismiss spinner and show success
            toast.dismiss(spinnerToast)
            toast.success(`File "${fileName}" opened successfully!`, {
              duration: 2000,
              style: {
                background: 'rgba(16, 185, 129, 0.5)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                color: 'white',
                borderRadius: '12px',
                padding: '12px 16px',
                fontSize: '14px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                border: '1px solid rgba(16, 185, 129, 0.3)'
              }
            })
            console.log('ViewNotePage: File processed successfully:', fileName)
            
          } else {
            // Handle backend file download (legacy)
            console.log('ViewNotePage: Attempting download from backend:', downloadUrl)
            const response = await fetch(downloadUrl, {
              headers: {
                'Authorization': localStorage.getItem('authToken') ? 
                  `Basic ${localStorage.getItem('authToken')}` : ''
              }
            })
            
            if (response.ok) {
              console.log('ViewNotePage: Download response successful')
              const blob = await response.blob()
              const url = window.URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = fileName
              a.style.display = 'none'
              document.body.appendChild(a)
              a.click()
              setTimeout(() => {
                window.URL.revokeObjectURL(url)
                document.body.removeChild(a)
              }, 100)
            } else {
              console.log('ViewNotePage: Download response failed:', response.status)
              toast.error('Failed to download file')
            }
          }
        } catch (error) {
          // Dismiss spinner and show error
          toast.dismiss(spinnerToast)
          console.error('ViewNotePage: Download failed:', error)
          toast.error(`Failed to open file: ${error.message}`, {
            duration: 5000,
            style: {
              background: 'rgba(239, 68, 68, 0.5)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              color: 'white',
              borderRadius: '12px',
              padding: '12px 16px',
              fontSize: '14px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              border: '1px solid rgba(239, 68, 68, 0.3)'
            }
          })
        }
        
        return false
      })
      
      console.log(`ViewNotePage: Click handler attached to link ${index}:`, fileName)
    })
  }

  // Ref for content container to process attachment links
  const contentRef = useRef(null)

  // Process attachment links when note content changes or layout mode changes
  useEffect(() => {
    if (contentRef.current && note?.content) {
      console.log('ViewNotePage: Processing attachment links for content:', note.content.substring(0, 100))
      console.log('ViewNotePage: Note object in useEffect:', {
        noteId: note.noteId,
        title: note.title,
        contentLength: note.content?.length,
        isEncrypted: note.isEncrypted,
        contentPreview: note.content?.substring(0, 50)
      })
      const timer = setTimeout(() => {
        processAttachmentLinks(contentRef)
      }, 100)
      
      return () => clearTimeout(timer)
    }
  }, [note?.content])

  // Debug log for note state changes
  useEffect(() => {
    if (note) {
      console.log('🔍 ViewNotePage: Note state changed:', {
        noteId: note.noteId,
        title: note.title,
        isEncrypted: note.isEncrypted,
        contentLength: note.content?.length,
        contentPreview: note.content?.substring(0, 50),
        contentType: typeof note.content,
        contentIsNull: note.content === null,
        contentIsUndefined: note.content === undefined,
        contentLooksEncrypted: note.content && /^[A-Za-z0-9+/]*={0,2}$/.test(note.content),
        contentLooksDecrypted: note.content && (note.content.includes('<') || note.content.includes('>') || note.content.length < 100)
      })
    }
  }, [note])

  // Global click handler for attachment links in view mode
  useEffect(() => {
    const handleAttachmentClick = (e) => {
      const link = e.target.closest('a')
      if (!link) return
      
      const href = link.getAttribute('href')
      const fileId = link.getAttribute('data-file-id')
      const fileName = link.getAttribute('data-file-name') || link.textContent
      const originalFileName = link.getAttribute('data-original-file-name')
      
      // Check if this is an attachment link that needs handling
      if ((href === 'about:blank' || href === 'javascript:void(0)') && fileName) {
        e.preventDefault()
        e.stopPropagation()
        
        console.log(`🌐 ViewNotePage Global handler: Attachment link clicked: ${fileName} (${fileId || 'no-file-id'})`)
        console.log(`🌐 ViewNotePage Global handler: Original filename: ${originalFileName}`)
        
        // Handle the download - use the actual filename for searching
        handleAttachmentDownload(fileId, fileName, link)
        return false
      }
    }

    document.addEventListener('click', handleAttachmentClick, true) // Use capture phase
    return () => document.removeEventListener('click', handleAttachmentClick, true)
  }, [])

  // Helper function to handle attachment downloads (similar to RichTextEditor)
  const handleAttachmentDownload = async (fileId, fileName, linkElement) => {
    let spinnerToast = null
    try {
      // Show floating spinner for file operations
      spinnerToast = toast.loading(
        `Opening ${fileName}...`, 
        {
          duration: Infinity, // Don't auto-dismiss
          style: {
            background: 'rgba(31, 41, 55, 0.5)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            color: 'white',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        }
      )
      
      // Check if this is a local file
      const isLocalFile = linkElement.getAttribute('data-local-file') === 'true' || 
                         linkElement.getAttribute('data-download-url')?.startsWith('local://')
      
      if (isLocalFile && isLocalMode) {
        // Handle local file download
        console.log('🌐 ViewNotePage Global handler: Downloading local file:', fileId)
        
        if (!getLocalFile) {
          throw new Error('Local file operations not available')
        }
        
        const localFile = await getLocalFile(fileId)
        const url = window.URL.createObjectURL(localFile.blob)
        
        // Handle MIME types for in-browser opening vs downloading
        const mimeType = localFile.fileType || localFile.mimeType
        
        // Files that should open in browser
        const browserOpenableTypes = [
          'text/plain',
          'text/markdown', 
          'text/html',
          'text/css',
          'text/javascript',
          'application/json',
          'application/pdf',
          'image/jpeg',
          'image/png',
          'image/gif',
          'image/webp',
          'image/svg+xml',
          'video/mp4',
          'video/webm',
          'audio/mp3',
          'audio/wav',
          'audio/webm',
          'audio/ogg',
          'audio/mpeg',
          'application/xml',
          'text/csv',
          'text/xml',
          'application/javascript',
          'text/javascript'
        ]
        
        // Check if MIME type is browser-openable (handle codec specifications like "audio/webm;codecs=opus")
        const baseMimeType = mimeType ? mimeType.split(';')[0].trim() : ''
        const isBrowserOpenable = browserOpenableTypes.includes(mimeType) || 
                                 browserOpenableTypes.includes(baseMimeType) ||
                                 (baseMimeType.startsWith('audio/') && browserOpenableTypes.some(type => type.startsWith('audio/')))
        
        console.log('🌐 ViewNotePage Global handler: MIME type check:', { mimeType, baseMimeType, isBrowserOpenable })
        
        if (isBrowserOpenable) {
          // Open in new tab
          console.log('🌐 ViewNotePage Global handler: Opening file in browser:', localFile.fileName)
          const newTab = window.open(url, '_blank')
          if (!newTab) {
            // Fallback if popup blocked
            console.log('🌐 ViewNotePage Global handler: Popup blocked, using fallback method')
            const a = document.createElement('a')
            a.href = url
            a.target = '_blank'
            a.click()
          }
        } else {
          // Force download for other file types
          console.log('🌐 ViewNotePage Global handler: Downloading file:', localFile.fileName)
          const a = document.createElement('a')
          a.href = url
          a.download = localFile.fileName
          a.style.display = 'none'
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
        }
        
        // Clean up after a delay
        setTimeout(() => {
          window.URL.revokeObjectURL(url)
        }, 1000)
        
        // Dismiss spinner and show success
        toast.dismiss(spinnerToast)
        toast.success(`File "${localFile.fileName}" opened successfully!`, {
          duration: 2000,
          style: {
            background: 'rgba(16, 185, 129, 0.5)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            color: 'white',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            border: '1px solid rgba(16, 185, 129, 0.3)'
          }
        })
        
      } else {
        // Handle Google Drive file download
        console.log('🌐 ViewNotePage Global handler: Downloading Google Drive file:', fileId)
        
        if (!window.googleDriveAPI || !window.googleDriveAPI.accessToken) {
          throw new Error('Google Drive API not available or not authenticated')
        }
        
        // First, always try to get from local cache (for offline access)
        try {
          // Clean the filename by removing emoji and extra characters
          const cleanFileName = fileName.replace(/^[🎵📎🎨📷📄🎬]+\s*/, '').trim()
          console.log('🌐 ViewNotePage Global handler: Checking local cache for file:', fileName, '-> cleaned:', cleanFileName)
          
          const cachedAttachment = await localStorageService.getAttachment(cleanFileName)
          if (cachedAttachment) {
            console.log('🌐 ViewNotePage Global handler: Found cached attachment:', cleanFileName)
            
            // Convert base64 back to blob
            const binaryString = atob(cachedAttachment.data)
            const bytes = new Uint8Array(binaryString.length)
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i)
            }
            const blob = new Blob([bytes], { type: cachedAttachment.fileType })
            
            // Handle MIME types for in-browser opening vs downloading
            const url = window.URL.createObjectURL(blob)
            let mimeType = cachedAttachment.fileType
            const finalFileName = cachedAttachment.fileName || cleanFileName
            
            // Files that should open in browser
            const browserOpenableTypes = [
              'text/plain',
              'text/markdown', 
              'text/html',
              'text/css',
              'text/javascript',
              'application/json',
              'application/pdf',
              'image/jpeg',
              'image/png',
              'image/gif',
              'image/webp',
              'image/svg+xml',
              'video/mp4',
              'video/webm',
              'audio/mp3',
              'audio/wav',
              'audio/webm',
              'audio/ogg',
              'audio/mpeg',
              'application/xml',
              'text/csv',
              'text/xml',
              'application/javascript',
              'text/javascript'
            ]
            
            console.log('🌐 ViewNotePage Global handler: Cached file MIME type:', mimeType, 'for file:', finalFileName)
            
            // Check if MIME type is browser-openable (handle codec specifications like "audio/webm;codecs=opus")
            const baseMimeType = mimeType ? mimeType.split(';')[0].trim() : ''
            const isBrowserOpenable = browserOpenableTypes.includes(mimeType) || 
                                     browserOpenableTypes.includes(baseMimeType) ||
                                     (baseMimeType.startsWith('audio/') && browserOpenableTypes.some(type => type.startsWith('audio/')))
            
            console.log('🌐 ViewNotePage Global handler: Cached MIME type check:', { mimeType, baseMimeType, isBrowserOpenable })
            
            if (isBrowserOpenable) {
              // Open in new tab
              console.log('🌐 ViewNotePage Global handler: Opening cached file in browser:', finalFileName)
              const newTab = window.open(url, '_blank')
              if (!newTab) {
                // Fallback if popup blocked
                console.log('🌐 ViewNotePage Global handler: Popup blocked, using fallback method')
                const a = document.createElement('a')
                a.href = url
                a.target = '_blank'
                a.click()
              }
            } else {
              // Force download for other file types
              console.log('🌐 ViewNotePage Global handler: Downloading cached file:', finalFileName)
              const a = document.createElement('a')
              a.href = url
              a.download = finalFileName
              a.style.display = 'none'
              document.body.appendChild(a)
              a.click()
              document.body.removeChild(a)
            }
            
            // Clean up after a delay
            setTimeout(() => {
              window.URL.revokeObjectURL(url)
            }, 1000)
            
            // Dismiss spinner and show success
            toast.dismiss(spinnerToast)
            toast.success(`File "${finalFileName}" opened from cache!`, {
              duration: 2000,
              style: {
                background: 'rgba(16, 185, 129, 0.5)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                color: 'white',
                borderRadius: '12px',
                padding: '12px 16px',
                fontSize: '14px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                border: '1px solid rgba(16, 185, 129, 0.3)'
              }
            })
            console.log('🌐 ViewNotePage Global handler: Cached file processed successfully:', finalFileName)
            return
          }
        } catch (cacheError) {
          console.log('🌐 ViewNotePage Global handler: No cached attachment found, proceeding with Google Drive:', cacheError.message)
        }
        
        // Use the same download logic as the existing processAttachmentLinks function
        let actualFileId = fileId
        
        // If no fileId or it's a temp ID, search for the file by name
        if (!actualFileId || actualFileId === 'null' || actualFileId === null || actualFileId.startsWith('temp-')) {
          // Clean the filename by removing emoji and extra characters
          const cleanFileName = fileName.replace(/^[🎵📎🎨📷📄🎬]+\s*/, '').trim()
          console.log('🌐 ViewNotePage Global handler: Searching for file by name in Google Drive:', fileName, '-> cleaned:', cleanFileName)
          
          // Search for the file by name (use cleaned filename)
          const searchQuery = `name='${cleanFileName}' and trashed=false`
          const searchResponse = await window.googleDriveAPI.apiRequest(`${window.googleDriveAPI.API_BASE}/files`, {
            q: searchQuery,
            fields: 'files(id,name,mimeType)'
          })
          
          const searchData = searchResponse.files ? searchResponse : await searchResponse.json()
          
          if (searchData.files && searchData.files.length > 0) {
            actualFileId = searchData.files[0].id
            console.log('🌐 ViewNotePage Global handler: Found file by search:', actualFileId)
          } else {
            // Fallback: search recent files with flexible matching
            console.log('🌐 ViewNotePage Global handler: Direct search failed, trying recent files with flexible matching')
            const recentFiles = await window.googleDriveAPI.apiRequest(`${window.googleDriveAPI.API_BASE}/files`, {
              q: 'trashed=false',
              fields: 'files(id,name,mimeType)',
              orderBy: 'modifiedTime desc',
              pageSize: 100
            })
            
            const recentData = recentFiles.files ? recentFiles : await recentFiles.json()
            console.log('🌐 ViewNotePage Global handler: Recent files sample:', recentData.files?.slice(0, 5).map(f => f.name))
            
            // Try exact match first (use cleaned filename)
            let matchingFile = recentData.files?.find(file => file.name === cleanFileName)
            
            if (matchingFile) {
              console.log('🌐 ViewNotePage Global handler: Found exact filename match:', matchingFile.name)
            } else {
              // Try case-insensitive match
              console.log('🌐 ViewNotePage Global handler: No exact match, trying case-insensitive search')
              matchingFile = recentData.files?.find(file => file.name.toLowerCase() === cleanFileName.toLowerCase())
              
              if (matchingFile) {
                console.log('🌐 ViewNotePage Global handler: Found case-insensitive match:', matchingFile.name)
              }
            }
            
            // If still no match, try partial match
            if (!matchingFile) {
              console.log('🌐 ViewNotePage Global handler: No case-insensitive match, trying partial search')
              const fileNameWithoutExt = cleanFileName.split('.')[0]
              matchingFile = recentData.files?.find(file =>
                file.name.toLowerCase().includes(fileNameWithoutExt.toLowerCase()) ||
                fileNameWithoutExt.toLowerCase().includes(file.name.split('.')[0].toLowerCase())
              )
            }

            // If still no match, try searching for files that start with the base filename (for timestamped files)
            if (!matchingFile) {
              console.log('🌐 ViewNotePage Global handler: No partial match, trying timestamped file search')
              const baseFileName = fileName.replace(/\.[^/.]+$/, '') // Remove extension
              const timestampedFiles = recentData.files?.filter(file => {
                const fileBaseName = file.name.replace(/\.[^/.]+$/, '') // Remove extension
                return fileBaseName.toLowerCase().startsWith(baseFileName.toLowerCase() + '_')
              })
              
              if (timestampedFiles && timestampedFiles.length > 0) {
                console.log(`🌐 ViewNotePage Global handler: Found ${timestampedFiles.length} timestamped files with base name '${baseFileName}':`, timestampedFiles.map(f => f.name))
                
                // If multiple files found, get the most recent one (files are already sorted by modifiedTime desc)
                matchingFile = timestampedFiles[0]
                console.log(`🌐 ViewNotePage Global handler: Selected most recent file: ${matchingFile.name}`)
                
                // Warn user if multiple files exist with same base name
                if (timestampedFiles.length > 1) {
                  console.warn(`⚠️ ViewNotePage Global handler: Multiple files found with base name '${baseFileName}'. Selected the most recent one: ${matchingFile.name}`)
                  console.warn(`⚠️ Available files: ${timestampedFiles.map(f => f.name).join(', ')}`)
                }
              }
            }
            
            if (matchingFile) {
              actualFileId = matchingFile.id
              console.log('🌐 ViewNotePage Global handler: Found file in recent files:', actualFileId, matchingFile.name)
            } else {
              console.log('🌐 ViewNotePage Global handler: Available files:', recentData.files?.map(f => f.name).slice(0, 10))
              throw new Error(`File not found: ${fileName}`)
            }
          }
        }
        
        const cloudFile = await window.googleDriveAPI.downloadAttachment(actualFileId)
        
        console.log('🌐 ViewNotePage Global handler: Download response:', cloudFile)
        console.log('🌐 ViewNotePage Global handler: Download response keys:', Object.keys(cloudFile || {}))
        console.log('🌐 ViewNotePage Global handler: Download response values:', {
          blob: cloudFile?.blob,
          fileName: cloudFile?.fileName,
          fileType: cloudFile?.fileType,
          fileSize: cloudFile?.fileSize,
          mimeType: cloudFile?.mimeType
        })
        
        // Handle MIME types for in-browser opening vs downloading
        const url = window.URL.createObjectURL(cloudFile.blob)
        let mimeType = cloudFile.fileType || cloudFile.mimeType
        const finalFileName = cloudFile.fileName || fileName
        
        // Fallback MIME type detection from file extension
        if (!mimeType && finalFileName) {
          const ext = finalFileName.split('.').pop().toLowerCase()
          const mimeTypes = {
            'md': 'text/markdown',
            'txt': 'text/plain',
            'html': 'text/html',
            'htm': 'text/html',
            'css': 'text/css',
            'js': 'text/javascript',
            'json': 'application/json',
            'pdf': 'application/pdf',
            'jpg': 'image/jpeg',
            'jpeg': 'image/jpeg',
            'png': 'image/png',
            'gif': 'image/gif',
            'webp': 'image/webp',
            'svg': 'image/svg+xml',
            'mp4': 'video/mp4',
            'webm': 'video/webm',
            'mp3': 'audio/mp3',
            'wav': 'audio/wav',
            'ogg': 'audio/ogg',
            'mpeg': 'audio/mpeg',
            'xml': 'application/xml',
            'csv': 'text/csv'
          }
          mimeType = mimeTypes[ext] || 'application/octet-stream'
          console.log('🌐 ViewNotePage Global handler: Detected MIME type from extension:', mimeType, 'for extension:', ext)
        }
        
        // Files that should open in browser
        const browserOpenableTypes = [
          'text/plain',
          'text/markdown', 
          'text/html',
          'text/css',
          'text/javascript',
          'application/json',
          'application/pdf',
          'image/jpeg',
          'image/png',
          'image/gif',
          'image/webp',
          'image/svg+xml',
          'video/mp4',
          'video/webm',
          'audio/mp3',
          'audio/wav',
          'audio/webm',
          'audio/ogg',
          'audio/mpeg',
          'application/xml',
          'text/csv',
          'text/xml',
          'application/javascript',
          'text/javascript'
        ]
        
        console.log('🌐 ViewNotePage Global handler: File MIME type:', mimeType, 'for file:', finalFileName)
        
        // Check if MIME type is browser-openable (handle codec specifications like "audio/webm;codecs=opus")
        const baseMimeType = mimeType ? mimeType.split(';')[0].trim() : ''
        const isBrowserOpenable = browserOpenableTypes.includes(mimeType) || 
                                 browserOpenableTypes.includes(baseMimeType) ||
                                 (baseMimeType.startsWith('audio/') && browserOpenableTypes.some(type => type.startsWith('audio/')))
        
        console.log('🌐 ViewNotePage Global handler: MIME type check:', { mimeType, baseMimeType, isBrowserOpenable })
        
        if (isBrowserOpenable) {
          // Open in new tab
          console.log('🌐 ViewNotePage Global handler: Opening file in browser:', finalFileName)
          const newTab = window.open(url, '_blank')
          if (!newTab) {
            // Fallback if popup blocked
            console.log('🌐 ViewNotePage Global handler: Popup blocked, using fallback method')
            const a = document.createElement('a')
            a.href = url
            a.target = '_blank'
            a.click()
          }
        } else {
          // Force download for other file types
          console.log('🌐 ViewNotePage Global handler: Downloading file:', finalFileName)
          const a = document.createElement('a')
          a.href = url
          a.download = finalFileName
          a.style.display = 'none'
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
        }
        
        // Clean up after a delay
        setTimeout(() => {
          window.URL.revokeObjectURL(url)
        }, 1000)
        
        // Dismiss spinner and show success
        toast.dismiss(spinnerToast)
        toast.success(`File "${finalFileName}" opened successfully!`, {
          duration: 2000,
          style: {
            background: 'rgba(16, 185, 129, 0.5)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            color: 'white',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            border: '1px solid rgba(16, 185, 129, 0.3)'
          }
        })
        console.log('🌐 ViewNotePage Global handler: File processed successfully:', finalFileName)
      }
      
    } catch (error) {
      // Dismiss spinner and show error
      if (spinnerToast) {
        toast.dismiss(spinnerToast)
      }
      console.error('🌐 ViewNotePage Global handler: Download failed:', error)
      toast.error(`Failed to open file: ${error.message}`, {
        duration: 5000,
        style: {
          background: 'rgba(239, 68, 68, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(239, 68, 68, 0.3)'
        }
      })
    }
  }

  // Create a stable content reference that handles all edge cases
  const contentToRender = useMemo(() => {
    // If no note, return null
    if (!note) {
      return null
    }
    
    // For sensitive notes, only use decrypted content (never fallback to encrypted content)
    if (isSensitiveNote(note)) {
      const isDecrypted = note.isEncrypted === false || note.isEncrypted === undefined
      const contentNotEncrypted = !isContentEncrypted(note.content)
      
      if (note && note.content && isDecrypted && contentNotEncrypted) {
        return note.content
      }
      
      return null
    }
    
    // For non-sensitive notes, use global cache if available, otherwise use note content
    const cachedContent = globalDecryptedContentCache.get(note.noteId)
    if (cachedContent) {
      return cachedContent
    }
    
    return note.content
  }, [note?.noteId, note?.isEncrypted, note?.content])

  /**
   * Process images in content - convert Google Drive URLs to base64 images
   */
  const processImagesInContent = async (content, isLocalMode) => {
    if (isLocalMode) {
      // Local mode - content should already have data URLs
      return content
    }

    // Cloud mode - find Google Drive URLs and convert to base64
    const googleDriveUrlRegex = /https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9-_]+)\/view/g
    let processedContent = content
    let match

    while ((match = googleDriveUrlRegex.exec(content)) !== null) {
      const fullUrl = match[0]
      const fileId = match[1]
      
      try {
        console.log('🔄 ViewNotePage: Processing Google Drive image:', fileId)
        const base64Data = await imageUploadService.getImageBase64(fullUrl, fileId)
        
        // Replace Google Drive URL with base64 data URL while preserving all image attributes
        const imgTagRegex = new RegExp(`<img([^>]+)src="${fullUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"([^>]*)>`, 'g')
        processedContent = processedContent.replace(imgTagRegex, (match, beforeSrc, afterSrc) => {
          // Extract width, height, and style attributes if they exist
          const widthMatch = match.match(/width="([^"]*)"/)
          const heightMatch = match.match(/height="([^"]*)"/)
          const styleMatch = match.match(/style="([^"]*)"/)
          
          let preservedAttributes = ''
          if (widthMatch) preservedAttributes += ` width="${widthMatch[1]}"`
          if (heightMatch) preservedAttributes += ` height="${heightMatch[1]}"`
          if (styleMatch) preservedAttributes += ` style="${styleMatch[1]}"`
          
          console.log(`🔍 ViewNotePage: Preserving image attributes:`, {
            width: widthMatch ? widthMatch[1] : 'none',
            height: heightMatch ? heightMatch[1] : 'none',
            style: styleMatch ? styleMatch[1] : 'none'
          })
          
          return `<img${beforeSrc}src="${base64Data}"${preservedAttributes}${afterSrc}>`
        })
        console.log('✅ ViewNotePage: Converted Google Drive URL to base64 with preserved attributes:', fileId)
      } catch (error) {
        console.error('❌ ViewNotePage: Failed to process image:', fileId, error)
        // Keep the original URL if processing fails
      }
    }

    return processedContent
  }

  /**
   * Render rich text content safely
   */
  const renderRichContent = useCallback(async (content) => {
    
    if (!content) {
      console.log('🔍 ViewNotePage: renderRichContent - no content, showing empty message')
      return <div className="text-gray-500 dark:text-gray-400 italic text-center py-8">This note has no content.</div>
    }
    
    // Ensure content is a string
    if (typeof content !== 'string') {
      console.warn('⚠️ ViewNotePage: Content is not a string:', typeof content, content)
      return <div className="text-gray-500 dark:text-gray-400 italic text-center py-8">Content format error.</div>
    }
    
    // If content contains HTML tags, render as HTML
    if (content.includes('<') && content.includes('>')) {
      // Process content to resolve Google Drive URLs to base64 images
      try {
        const processedContent = await processImagesInContent(content, isLocalMode)
        console.log('🖼️ ViewNotePage: Processed content for display:', processedContent.substring(0, 200) + '...')
        
        // Make content read-only for view mode
        const readOnlyContent = makeContentReadOnly(processedContent)
        
        return (
          <div
            ref={contentRef}
            className="rich-text-display"
            dangerouslySetInnerHTML={{ __html: readOnlyContent }}
          />
        )
      } catch (error) {
        console.error('❌ ViewNotePage: Error processing images:', error)
        
        // Fallback: render content as-is but make it read-only
        const readOnlyContent = makeContentReadOnly(content)
        return (
          <div
            ref={contentRef}
            className="rich-text-display"
            dangerouslySetInnerHTML={{ __html: readOnlyContent }}
          />
        )
      }
    } else {
      console.log('🔍 ViewNotePage: renderRichContent - rendering as plain text')
      // Plain text content - preserve line breaks
      return (
        <div ref={contentRef} className="rich-text-display">
          {content.split('\n').map((line, index) => (
            <p key={index} className="mb-3">
              {line || '\u00A0'} {/* Non-breaking space for empty lines */}
            </p>
          ))}
        </div>
      )
    }
  }, [isLocalMode])

  // Process content for image references when content changes
  useEffect(() => {
    const processContent = async () => {
      if (!contentToRender) {
        setRenderedContent(null)
        return
      }

      try {
        const processed = await renderRichContent(contentToRender)
        setRenderedContent(processed)
      } catch (error) {
        console.error('❌ ViewNotePage: Error processing content:', error)
        setRenderedContent(null)
      }
    }

    processContent()
  }, [contentToRender, renderRichContent])

  /**
   * Get word and character count from content
   */
  const getContentStats = (content) => {
    if (!content) return { characters: 0, words: 0 }
    
    // Remove HTML tags for counting
    const plainText = content.replace(/<[^>]*>/g, '')
    const characters = plainText.length
    const words = plainText.trim() ? plainText.trim().split(/\s+/).filter(word => word.length > 0).length : 0
    
    return { characters, words }
  }

  // Check for invalid ID
  if (!noteId || noteId === 'undefined') {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Invalid Note ID</h3>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          The note ID is missing or invalid.
        </p>
        <Link to="/notes" className="btn-primary">
          Back to Notes
        </Link>
      </div>
    )
  }

  // Show loading spinner while loading note data
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Show error if note data couldn't be loaded
  if (!note) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Note not found</h3>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          The note you're looking for doesn't exist or has been deleted.
        </p>
        <Link to="/notes" className="btn-primary">
          Back to Notes
        </Link>
      </div>
    )
  }

  const contentStats = getContentStats(note.content)

  // Check if this is a flowchart note and render with specialized viewer
  const noteType = detectNoteType(note)
  if (noteType === 'FLOWCHART') {
    return (
      <FlowchartNoteViewer 
        note={note}
        noteId={noteId}
        tabId={tabId}
        isInTab={isInTab}
      />
    )
  }

  return (
    <div className="space-y-6 note-view-container">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        {!isInTab && (
          <Link
            to="/notes"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Notes
          </Link>
        )}
        {isInTab && <div />} {/* Spacer for flex layout */}
        <div className="flex space-x-3">
          <button
            onClick={handleExport}
            className="btn-outline inline-flex items-center"
            title="Export"
          >
            <Download className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Export</span>
          </button>
          <button
            onClick={() => setChatModalOpen(true)}
            className="bg-green-500 hover:bg-green-600 text-white border border-green-500 hover:border-green-600 px-4 py-2 rounded-md font-medium transition-colors duration-200 inline-flex items-center"
            title="Chat with Note"
          >
            <MessageCircle className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Chat with Note</span>
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="btn-danger inline-flex items-center"
            title={isDeleting ? "Deleting..." : "Delete"}
          >
            {isDeleting ? (
              <>
                <LoadingSpinner size="sm" className="md:mr-2" />
                <span className="hidden md:inline">Deleting...</span>
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Delete</span>
              </>
            )}
          </button>
          {!isSensitiveNote(note) && (
            <button
              onClick={handleEdit}
              className="btn-primary inline-flex items-center"
              title="Edit Note"
            >
              <Edit className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Edit Note</span>
            </button>
          )}
        </div>
      </div>

      {/* Note Content */}
      <div className="card">
        <div className="card-body relative">
          {/* Note Title */}
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4 leading-tight">
            {note.title || 'Untitled Note'}
          </h1>

          {/* Note Metadata */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <span className="font-medium mr-1">Created:</span>
              {formatDate(note.createdAt)}
            </div>
            {note.lastModified && note.lastModified !== note.createdAt && (
              <div className="flex items-center">
                <span className="font-medium mr-1">Last modified:</span>
                {formatDate(note.lastModified)}
              </div>
            )}
            <div className="flex items-center">
              <span className="font-medium mr-1">Characters:</span>
              {contentStats.characters}
            </div>
            <div className="flex items-center">
              <span className="font-medium mr-1">Words:</span>
              {contentStats.words}
            </div>
          </div>

          {/* Note Content */}
          <div key={renderKey} className="prose max-w-none note-content-area">
            {renderedContent || (
              <div className="text-gray-500 dark:text-gray-400 italic text-center py-8">
                Processing content...
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Note Actions */}
      <div className="card">
        <div className="card-body">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Actions</h3>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleExport}
              className="btn-outline inline-flex items-center"
            >
              <Download className="h-4 w-4 mr-2" />
              Export this note
            </button>
            <button
              onClick={() => setChatModalOpen(true)}
              className="bg-green-500 hover:bg-green-600 text-white border border-green-500 hover:border-green-600 px-4 py-2 rounded-md font-medium transition-colors duration-200 inline-flex items-center"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Chat with Note
            </button>
            {!isSensitiveNote(note) && (
              <button
                onClick={handleEdit}
                className="btn-primary inline-flex items-center"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit this note
              </button>
            )}
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="btn-danger inline-flex items-center"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete this note
            </button>
            {isInTab ? (
              <button
                onClick={() => {
                  // Generate a unique ID for each new note tab
                  const uniqueId = `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
                  
                  // Count existing new note tabs to create unique title
                  const newNoteCount = tabs.filter(tab => tab.type === 'new').length + 1
                  const uniqueTitle = `New Note ${newNoteCount}`
                  
                  openTab({
                    type: 'new',
                    noteId: uniqueId,
                    title: uniqueTitle,
                    note: null
                  })
                }}
                className="btn-outline inline-flex items-center"
              >
                Create new note
              </button>
            ) : (
              <Link
                to="/notes/new"
                className="btn-outline inline-flex items-center"
              >
                Create new note
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Password prompt is now handled via browser popup */}
      
      {/* Chat with Note Modal */}
      <ChatWithNoteModal
        isOpen={isChatModalOpen}
        onClose={() => setChatModalOpen(false)}
        note={note}
        noteTitle={note?.title}
      />
    </div>
  )
}

export default ViewNotePage 